import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  State,
  StateFilter,
  StateResponse,
  StatePayload,
} from "../types/states.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.states;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const statesService = {
  async list(filter: StateFilter): Promise<StateResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<StateResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load state list.") as unknown as StateResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<State>> {
    try {
      return await apiClient<ApiResponse<State>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load state details.") as unknown as ApiResponse<State>;
    }
  },

  async create(payload: StatePayload): Promise<ApiResponse<State>> {
    try {
      return await apiClient<ApiResponse<State>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create state.") as unknown as ApiResponse<State>;
    }
  },

  async update(id: number | string, payload: Partial<StatePayload>): Promise<ApiResponse<State>> {
    try {
      return await apiClient<ApiResponse<State>>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to update state.") as unknown as ApiResponse<State>;
    }
  },

  async delete(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to delete state.") as unknown as ApiResponse<unknown>;
    }
  },
};
